import { useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { WaypointItem } from './WaypointItem';
import type { Waypoint } from '../lib/types';

interface WaypointListProps {
  waypoints: Waypoint[];
  onReorder: (waypoints: Waypoint[]) => void;
  onRemove: (id: string) => void;
}

export function WaypointList({ waypoints, onReorder, onRemove }: WaypointListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = waypoints.findIndex((w) => w.id === active.id);
        const newIndex = waypoints.findIndex((w) => w.id === over.id);
        const newWaypoints = arrayMove(waypoints, oldIndex, newIndex);
        onReorder(newWaypoints);
      }
    },
    [waypoints, onReorder]
  );

  if (waypoints.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p className="text-sm">Добавьте точки маршрута</p>
        <p className="text-xs mt-1">через поиск или из списка визитов</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={waypoints} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {waypoints.map((waypoint, index) => (
            <WaypointItem
              key={waypoint.id}
              waypoint={waypoint}
              index={index}
              onRemove={onRemove}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
